import { useRef } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/cn';
import { selectLive, useLiveStories } from '@/features/stories/api';
import { useSites } from '../api';
import { SiteTile } from './SiteTile';

/**
 * The row of round site tiles across the top of the home page.
 *
 * Scrolling is native overflow with scroll-snap; the arrows nudge it. A JS
 * carousel would break keyboard and touch behaviour that the browser already
 * gets right.
 */
export function FeaturedSitesStrip() {
  const { data: sites, isLoading } = useSites(true);
  const { data: storyData } = useLiveStories();
  const trackRef = useRef<HTMLDivElement>(null);

  // Two queries rather than a `hasStory` flag on the site: the site list changes
  // a few times a month and is cached for half an hour, while stories start and
  // end on a schedule. Folding them together would mean either caching the sites
  // for a minute or leaving a ring lit on a finished campaign.
  const liveSlugs = new Set(selectLive(storyData).map((story) => story.siteSlug));

  function nudge(direction: -1 | 1) {
    trackRef.current?.scrollBy({
      left: direction * 320,
      behavior:
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'instant'
          : 'smooth',
    });
  }

  // Nothing published yet is the only case that hides the section entirely.
  // While it loads the heading stays, so the page's outline does not change
  // shape underneath a screen reader mid-render.
  if (!isLoading && (!sites || sites.length === 0)) {
    return null;
  }

  return (
    <section aria-labelledby="featured-sites" className="relative">
      <h2 id="featured-sites" className="section-title mb-4 flex items-center gap-2">
        <CrownGlyph />
        Öne Çıkan
      </h2>

      {isLoading ? (
        <div className="flex gap-3 pt-2 pb-2 sm:gap-4 md:px-7" aria-hidden>
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="flex w-20 shrink-0 flex-col items-center gap-2 sm:w-24">
              <div className="size-[68px] animate-pulse rounded-full bg-surface-800 sm:size-[78px]" />
              <div className="h-3 w-12 animate-pulse rounded bg-surface-800" />
            </div>
          ))}
        </div>
      ) : (
        <div className="relative">
          {/* Aligned to the CIRCLE's centre, not the track's: the track is the
              circle plus its caption, so top-1/2 would sit the arrows a caption's
              half-height too low. 0.5rem is the track's pt-2, 39px half of the
              78px circle these widths use. */}
          <button
            type="button"
            onClick={() => nudge(-1)}
            aria-label="Öncekiler"
            className="absolute left-0 top-[calc(0.5rem+39px)] z-10 hidden -translate-y-1/2 rounded-full border border-surface-700 bg-surface-900 p-1.5 text-surface-300 shadow-lg shadow-surface-950/60 transition hover:text-surface-50 md:block"
          >
            <ChevronGlyph className="rotate-180" />
          </button>

          <div
            ref={trackRef}
            className={cn(
              'flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth sm:gap-6 [&>*]:snap-start',
              // `overflow-x: auto` computes overflow-y to `auto` as well — the
              // axes cannot differ — so anything drawn above the track's top
              // edge is CLIPPED, not overflowing. The tile badges sit at -top-1,
              // which is why they came out shaved along the top. The room has to
              // be inside the scroll box.
              'pt-2 pb-2',
              // Gutters the arrows sit in. Without them the left arrow is drawn
              // on top of the first tile, over the very circle it is meant to
              // scroll away from.
              'md:px-7',
              // The gutters are padding, so scroll-snap would still park a tile
              // flush against the container edge, back under the arrow.
              'md:scroll-px-7',
            )}
          >
            {sites?.map((site) => (
              <SiteTile
                key={site.id}
                site={site}
                placement="strip"
                hasStory={liveSlugs.has(site.slug)}
              />
            ))}

            <Link
              to="/trusted-sites"
              className="group flex w-20 shrink-0 flex-col items-center gap-2 sm:w-24"
            >
              <span className="grid size-[68px] place-items-center rounded-full border border-dashed border-surface-700 text-surface-500 transition group-hover:border-brand-500 group-hover:text-brand-400 sm:size-[78px]">
                <span aria-hidden className="text-xl">
                  ⋯
                </span>
              </span>
              <span className="text-center text-xs text-surface-300 group-hover:text-surface-100">
                Tümü
              </span>
            </Link>
          </div>

          <button
            type="button"
            onClick={() => nudge(1)}
            aria-label="Sonrakiler"
            className="absolute right-0 top-[calc(0.5rem+39px)] z-10 hidden -translate-y-1/2 rounded-full border border-surface-700 bg-surface-900 p-1.5 text-surface-300 shadow-lg shadow-surface-950/60 transition hover:text-surface-50 md:block"
          >
            <ChevronGlyph />
          </button>
        </div>
      )}
    </section>
  );
}

function CrownGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 fill-current">
      <path d="M3 8l4 3 5-6 5 6 4-3-2 11H5L3 8z" />
    </svg>
  );
}

function ChevronGlyph({ className }: { className?: string }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className={`size-4 fill-current ${className ?? ''}`}>
      <path d="M9 6l6 6-6 6z" />
    </svg>
  );
}
